import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * GP-308 — Recent Patient Searches store.
 *
 * Persists the last N patients the current browser has viewed via the global
 * patient-search dialog (either quick-view click or "Open Patient360" click —
 * both count). Surfaced at the top of the dialog when the search field is
 * empty so staff can re-find "who was I just looking at" without retyping.
 *
 * Storage: `localStorage` via Zustand `persist`. Per-browser, survives refresh
 * and logout. Cross-device sync was considered but explicitly deferred per
 * Walid (GP-308 walkthrough 2026-05-13) — the round-trip on every dialog open
 * isn't worth it for the use case.
 */

export const RECENT_PATIENT_SEARCHES_CAP = 20;

export interface RecentPatientSearchEntry {
  /** Composite ID — `${pharmacyId}_${krollPatientId}` — same key used by Patient 360 routes. */
  patientId: string;
  /** Pharmacy the patient belongs to (the one that was selected when they were viewed). */
  pharmacyId: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  /** ISO timestamp of the most recent view. */
  searchedAt: string;
}

interface RecentPatientSearchesState {
  entries: RecentPatientSearchEntry[];
  /** Add or refresh an entry. Dedupes by `patientId`, moves to top, trims to cap. */
  record: (entry: Omit<RecentPatientSearchEntry, 'searchedAt'>) => void;
  /** Clear all history (UI exposes this as a "Clear" button). */
  clear: () => void;
}

export const useRecentPatientSearchesStore = create<RecentPatientSearchesState>()(
  devtools(
    persist(
      (set) => ({
        entries: [],
        record: (entry) =>
          set((state) => {
            const now = new Date().toISOString();
            const filtered = state.entries.filter((e) => e.patientId !== entry.patientId);
            const next: RecentPatientSearchEntry[] = [
              { ...entry, searchedAt: now },
              ...filtered,
            ].slice(0, RECENT_PATIENT_SEARCHES_CAP);
            return { entries: next };
          }),
        clear: () => set({ entries: [] }),
      }),
      {
        name: 'recent-patient-searches',
        version: 1,
      },
    ),
    { name: 'RecentPatientSearches' },
  ),
);
